/**
 * CorrelationGraph
 *
 * Renders a ReactFlow graph showing how a correlate-batch risk relates to
 * the discovered asset and the provider resource(s) it was correlated against.
 *
 * Layout:
 *   [Provider Resource] ──CORRELATED_WITH──▶ [Asset] ──HAS_VULNERABILITY──▶ [Risk]
 *
 * Only rendered when risk.source === 'correlate-batch'.
 * Follows the same visual style as the AssetRelationshipGraph in the Asset drawer.
 */

import {
  Background,
  ControlButton,
  Controls,
  type Edge,
  Handle,
  type Node,
  PanOnScrollMode,
  Position,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from '@xyflow/react';
import {
  type CSSProperties,
  memo,
  useCallback,
  useEffect,
  useState,
} from 'react';

import { Spinner } from '@/components/ui/spinner';
import type { CorrelationNode } from '@/sections/vulnerabilities/hooks/drawer/useCorrelationGraphData';
import { useCorrelationGraphData } from '@/sections/vulnerabilities/hooks/drawer/useCorrelationGraphData';
import type { Risk } from '@/types';
import { cn } from '@/utils/classname';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const NODE_WIDTH = 280;
const NODE_HEIGHT = 56;
const X_GAP = 100;

const SEVERITY_LABELS: Record<string, string> = {
  C: 'Critical',
  H: 'High',
  M: 'Medium',
  L: 'Low',
};

function severityLabel(statusChar: string | undefined): string {
  return (statusChar && SEVERITY_LABELS[statusChar]) || 'Info';
}

// ---------------------------------------------------------------------------
// Custom node types
// ---------------------------------------------------------------------------

interface CorrelationNodeData {
  label: string;
  sublabel?: string;
  variant: 'asset' | 'risk' | 'provider' | 'empty-provider';
}

function CorrelationNodeComponent({ data }: { data: CorrelationNodeData }) {
  const variantStyles: Record<CorrelationNodeData['variant'], string> = {
    asset: 'border-chariot-brand-primary bg-chariot-brand-primary/5',
    risk: 'border-chariot-status-critical bg-chariot-status-critical/5',
    provider: 'border-chariot-status-success bg-chariot-status-success/5',
    'empty-provider':
      'border-chariot-status-medium border-dashed bg-chariot-status-medium/5',
  };

  return (
    <div
      className={cn(
        'flex h-full flex-col justify-center overflow-hidden rounded-lg border-2 px-3 py-2',
        variantStyles[data.variant]
      )}
    >
      <Handle
        className="bg-chariot-stroke ml-[1.5px] size-2.5"
        type="target"
        position={Position.Left}
      />
      <div className="text-chariot-text-primary truncate text-sm font-medium">
        {data.label}
      </div>
      {data.sublabel && (
        <div className="text-chariot-text-secondary truncate text-xs">
          {data.sublabel}
        </div>
      )}
      <Handle
        className="bg-chariot-stroke mr-[1.5px] size-2.5"
        type="source"
        position={Position.Right}
      />
    </div>
  );
}

const nodeTypes = {
  correlation: memo(CorrelationNodeComponent),
};

// ---------------------------------------------------------------------------
// Edge label styles
// ---------------------------------------------------------------------------

const EDGE_LABEL_STYLE: CSSProperties = {
  fontSize: 10,
  fill: 'rgb(var(--chariot-text-secondary))',
  fontWeight: 500,
};

const EDGE_LABEL_BG_STYLE: CSSProperties = {
  fill: 'rgb(var(--chariot-background-primary))',
  fillOpacity: 0.85,
};

// ---------------------------------------------------------------------------
// Graph builder
// ---------------------------------------------------------------------------

function buildGraph(
  risk: Risk,
  correlationData: CorrelationNode
): { nodes: Node[]; edges: Edge[] } {
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  // Centre asset node
  const assetLabel = correlationData.asset?.name ?? risk.dns;
  const assetClass = correlationData.asset?.class ?? '';
  nodes.push({
    id: 'asset',
    type: 'correlation',
    position: { x: NODE_WIDTH + X_GAP, y: 0 },
    data: {
      label: assetLabel,
      sublabel: assetClass ? `Asset · ${assetClass}` : 'Asset',
      variant: 'asset',
    } satisfies CorrelationNodeData,
    width: NODE_WIDTH,
    height: NODE_HEIGHT,
    connectable: false,
    draggable: false,
  });

  // Risk node (right)
  nodes.push({
    id: 'risk',
    type: 'correlation',
    position: { x: (NODE_WIDTH + X_GAP) * 2, y: 0 },
    data: {
      label: risk.name,
      sublabel: `Risk · ${severityLabel(risk.status?.[1])}`,
      variant: 'risk',
    } satisfies CorrelationNodeData,
    width: NODE_WIDTH,
    height: NODE_HEIGHT,
    connectable: false,
    draggable: false,
  });

  edges.push({
    id: 'asset-risk',
    source: 'asset',
    target: 'risk',
    type: 'smoothstep',
    animated: true,
    label: 'HAS_VULNERABILITY',
    labelStyle: EDGE_LABEL_STYLE,
    labelBgStyle: EDGE_LABEL_BG_STYLE,
    labelBgPadding: [6, 3] as [number, number],
    labelBgBorderRadius: 4,
    className: '[&_path]:stroke-chariot-status-critical/60 [&_path]:stroke-1',
  });

  // Provider nodes (left)
  if (correlationData.providers.length > 0) {
    const totalHeight =
      correlationData.providers.length * NODE_HEIGHT +
      (correlationData.providers.length - 1) * 16;
    const startY = -(totalHeight / 2) + NODE_HEIGHT / 2;

    correlationData.providers.forEach((provider, index) => {
      const id = `provider-${index}`;
      nodes.push({
        id,
        type: 'correlation',
        position: { x: 0, y: startY + index * (NODE_HEIGHT + 16) },
        data: {
          label: provider.displayName || provider.name,
          sublabel:
            [provider.provider, provider.resourceType]
              .filter(Boolean)
              .join(' · ') || 'Provider resource',
          variant: 'provider',
        } satisfies CorrelationNodeData,
        width: NODE_WIDTH,
        height: NODE_HEIGHT,
        connectable: false,
        draggable: false,
      });

      edges.push({
        id: `${id}-asset`,
        source: id,
        target: 'asset',
        type: 'smoothstep',
        animated: true,
        label: 'CORRELATED_WITH',
        labelStyle: EDGE_LABEL_STYLE,
        labelBgStyle: EDGE_LABEL_BG_STYLE,
        labelBgPadding: [6, 3] as [number, number],
        labelBgBorderRadius: 4,
        className:
          '[&_path]:stroke-chariot-status-success/60 [&_path]:stroke-1',
      });
    });

    // Re-centre asset vertically when providers exist
    const assetNode = nodes.find(n => n.id === 'asset')!;
    assetNode.position.y =
      0 +
      (startY +
        ((correlationData.providers.length - 1) * (NODE_HEIGHT + 16)) / 2);
    const riskNode = nodes.find(n => n.id === 'risk')!;
    riskNode.position.y = assetNode.position.y;
  } else {
    // No provider correlated → show a dashed "unclaimed" placeholder
    nodes.push({
      id: 'empty-provider',
      type: 'correlation',
      position: { x: 0, y: 0 },
      data: {
        label: 'No provider resource found',
        sublabel: 'Dangling — resource unclaimed',
        variant: 'empty-provider',
      } satisfies CorrelationNodeData,
      width: NODE_WIDTH,
      height: NODE_HEIGHT,
      connectable: false,
      draggable: false,
    });

    edges.push({
      id: 'empty-provider-asset',
      source: 'empty-provider',
      target: 'asset',
      type: 'smoothstep',
      animated: false,
      label: 'NOT CORRELATED',
      labelStyle: EDGE_LABEL_STYLE,
      labelBgStyle: EDGE_LABEL_BG_STYLE,
      labelBgPadding: [6, 3] as [number, number],
      labelBgBorderRadius: 4,
      style: { strokeDasharray: '6,4' },
      className: '[&_path]:stroke-chariot-status-medium/60 [&_path]:stroke-1',
    });
  }

  return { nodes, edges };
}

// ---------------------------------------------------------------------------
// Inner graph (must be inside ReactFlowProvider)
// ---------------------------------------------------------------------------

function CorrelationGraphInner({
  risk,
  correlationData,
}: {
  risk: Risk;
  correlationData: CorrelationNode;
}) {
  const { fitView } = useReactFlow();
  const [isReady, setIsReady] = useState(false);

  const { nodes: initialNodes, edges: initialEdges } = buildGraph(
    risk,
    correlationData
  );

  const [nodes, , onNodesChange] = useNodesState<Node>(initialNodes);
  const [edges, , onEdgesChange] = useEdgesState<Edge>(initialEdges);

  const reCenter = useCallback(() => {
    fitView({ duration: 400, padding: 0.3 });
  }, [fitView]);

  useEffect(() => {
    window.requestAnimationFrame(() => {
      fitView({ duration: 0, padding: 0.3 });
      setIsReady(true);
    });
  }, [fitView]);

  return (
    <div className="relative size-full overflow-hidden">
      <ReactFlow
        id="correlation"
        nodes={nodes}
        edges={edges}
        panOnScroll
        fitView
        elementsSelectable={false}
        nodesConnectable={false}
        nodesDraggable={false}
        zoomOnScroll={false}
        panOnScrollMode={PanOnScrollMode.Vertical}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        maxZoom={1.5}
        minZoom={0.3}
        className={cn('size-full bg-transparent', !isReady && 'opacity-0')}
      >
        <Controls
          fitViewOptions={{ duration: 400, padding: 0.3 }}
          showInteractive={false}
          className="border-chariot-stroke bg-chariot-background-primary/90 [&_button]:border-b-chariot-stroke [&_button]:text-chariot-text-primary hover:[&_button]:bg-chariot-background-secondary/60 rounded-lg border-2 backdrop-blur-sm [&_button]:border-b-2 [&_button]:bg-transparent [&_button]:transition-all [&_button]:duration-200 last:[&_button]:border-b-0"
        >
          <ControlButton onClick={reCenter}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="32"
              height="30"
              viewBox="0 0 32 30"
              fill="none"
            >
              <g clipPath="url(#clip0_corr)">
                <path
                  d="M3.692 4.63005C3.692 4.10005 4.092 3.69205 4.631 3.69205H9.846V5.23661e-05H4.708C2.13 5.23661e-05 0 2.05405 0 4.63005V9.84605H3.692V4.63005ZM27.354 5.23661e-05H22.154V3.69205H27.324C27.854 3.69205 28.308 4.09205 28.308 4.63105V9.84605H32V4.63105C32.0009 4.02138 31.8813 3.41753 31.6479 2.85428C31.4146 2.29102 31.0722 1.77947 30.6404 1.34906C30.2086 0.918646 29.6959 0.577878 29.1319 0.346362C28.5679 0.114845 27.9637 -0.00284906 27.354 5.23661e-05ZM28.308 24.8301C28.308 25.3621 27.908 25.7701 27.369 25.7701H22.154V29.5381H27.369C29.946 29.5381 32 27.4081 32 24.8311V19.6921H28.308V24.8301ZM4.631 25.7701C4.1 25.7701 3.692 25.3701 3.692 24.8301V19.6921H0V24.8311C0 27.4081 2.13 29.5381 4.708 29.5381H9.846V25.7701H4.631Z"
                  fill="currentColor"
                />
                <circle cx="16" cy="15" r="5" fill="currentcolor" />
              </g>
              <defs>
                <clipPath id="clip0_corr">
                  <rect width="32" height="30" fill="currentcolor" />
                </clipPath>
              </defs>
            </svg>
          </ControlButton>
        </Controls>
        <Background color="rgb(var(--chariot-stroke) / 0.3)" size={4} />
      </ReactFlow>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

export function CorrelationGraph({ risk }: { risk: Risk }) {
  const { correlationData, isLoading, isCorrelationRisk } =
    useCorrelationGraphData(risk);

  if (!isCorrelationRisk) return null;

  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-chariot-text-secondary text-sm font-semibold">
        Correlation Map
      </h3>
      <div className="border-chariot-stroke h-64 min-h-64 w-full overflow-hidden rounded-md border">
        {isLoading ? (
          <Spinner centered size="xl" />
        ) : (
          <ReactFlowProvider>
            <CorrelationGraphInner
              risk={risk}
              correlationData={correlationData}
            />
          </ReactFlowProvider>
        )}
      </div>
    </div>
  );
}
